import React from "react";
import { useMutation, useQueryClient } from "react-query";
import {
    Typography,
    Box,
    Button,
    Stack,
    Divider,
    Card,
    CardActions,
    CardContent,
    Chip,
    Select,
    Option,
} from "@mui/joy";
import { EditNoteOutlined } from "@mui/icons-material";
import { ApiConstructabilityIssue } from "../../shared/interfaces";
import { ConflictLikelihood, ConflictLikelihoodChip } from "./ConflictLikelihoodChip";
import { IssueStatus, IssueStatusChip, ISSUE_STATUS } from "./IssueStatusChip";
import { BallInCourt, BallInCourtChip } from "./BallInCourtChip";
import { MarkdownRenderer } from "../MarkdownRenderer";
import { ProblemNode } from "./ProblemNode";
import { FileViewerArgs } from "../fileviewer/FileViewer";
import { WorkflowApi } from "../../api/workflow";
import { PrimeQueryKeys } from "../../shared/constants";

interface IssueCardProps {
    issue: ApiConstructabilityIssue;
    index: number;
    isDismissed?: boolean;
    onRfiClick: (issue: ApiConstructabilityIssue) => void;
    formatUpdatedInfo: (issue: ApiConstructabilityIssue) => string | null;
    currentFileViewerFile: FileViewerArgs | null;
    setCurrentFileViewerFile: React.Dispatch<React.SetStateAction<FileViewerArgs | null>>;
}

const styles = {
    issueCard: {
        m: 2,
        pb: 1.5,
    },
    issueTitle: {
        fontFamily: "Overpass",
    },
    previewContainer: {
        display: "flex",
        gap: 2,
        mt: 2,
    },
    draftRfiButton: {
        justifyContent: "center",
    },
};

export default function IssueCard({
    issue,
    index,
    isDismissed = false,
    onRfiClick,
    formatUpdatedInfo,
    currentFileViewerFile,
    setCurrentFileViewerFile,
}: IssueCardProps) {
    const queryClient = useQueryClient();

    // Mutations for updating issue properties with optimistic updates
    const updateSeverityMutation = useMutation(
        ({ id, severity }: { id: string; severity: ConflictLikelihood }) =>
            WorkflowApi.updateConstructabilityIssueSeverity(id, severity),
        {
            onMutate: async ({ id, severity }) => {
                // Cancel outgoing refetches
                await queryClient.cancelQueries(PrimeQueryKeys.workflowProblems);

                // Snapshot the previous value
                const previousIssues = queryClient.getQueryData<ApiConstructabilityIssue[]>(
                    PrimeQueryKeys.workflowProblems,
                );

                // Optimistically update to the new value
                queryClient.setQueryData<ApiConstructabilityIssue[]>(
                    PrimeQueryKeys.workflowProblems,
                    (old) =>
                        old?.map((issue) =>
                            issue.id === id ? { ...issue, severity } : issue,
                        ) || [],
                );

                // Return a context object with the snapshotted value
                return { previousIssues };
            },
            onError: (err, variables, context) => {
                // If the mutation fails, use the context returned from onMutate to roll back
                queryClient.setQueryData(PrimeQueryKeys.workflowProblems, context?.previousIssues);
                console.error("Failed to update severity:", err);
            },
            onSettled: () => {
                // Always refetch after error or success to ensure server state
                queryClient.invalidateQueries(PrimeQueryKeys.workflowProblems);
            },
        },
    );

    const updateStatusMutation = useMutation(
        ({ id, status }: { id: string; status: IssueStatus }) =>
            WorkflowApi.updateConstructabilityIssueStatus(id, status),
        {
            onMutate: async ({ id, status }) => {
                // Cancel outgoing refetches
                await queryClient.cancelQueries(PrimeQueryKeys.workflowProblems);

                // Snapshot the previous value
                const previousIssues = queryClient.getQueryData<ApiConstructabilityIssue[]>(
                    PrimeQueryKeys.workflowProblems,
                );

                // Optimistically update to the new value
                queryClient.setQueryData<ApiConstructabilityIssue[]>(
                    PrimeQueryKeys.workflowProblems,
                    (old) =>
                        old?.map((issue) =>
                            issue.id === id ? { ...issue, status } : issue,
                        ) || [],
                );

                // Return a context object with the snapshotted value
                return { previousIssues };
            },
            onError: (err, variables, context) => {
                // If the mutation fails, use the context returned from onMutate to roll back
                queryClient.setQueryData(PrimeQueryKeys.workflowProblems, context?.previousIssues);
                console.error("Failed to update status:", err);
            },
            onSettled: () => {
                // Always refetch after error or success to ensure server state
                queryClient.invalidateQueries(PrimeQueryKeys.workflowProblems);
            },
        },
    );

    const updateBallInCourtMutation = useMutation(
        ({ id, ballInCourt }: { id: string; ballInCourt: BallInCourt }) =>
            WorkflowApi.updateConstructabilityIssueBallInCourt(id, ballInCourt),
        {
            onMutate: async ({ id, ballInCourt }) => {
                // Cancel outgoing refetches
                await queryClient.cancelQueries(PrimeQueryKeys.workflowProblems);

                // Snapshot the previous value
                const previousIssues = queryClient.getQueryData<ApiConstructabilityIssue[]>(
                    PrimeQueryKeys.workflowProblems,
                );

                // Optimistically update to the new value
                queryClient.setQueryData<ApiConstructabilityIssue[]>(
                    PrimeQueryKeys.workflowProblems,
                    (old) =>
                        old?.map((issue) =>
                            issue.id === id ? { ...issue, ballInCourt } : issue,
                        ) || [],
                );

                // Return a context object with the snapshotted value
                return { previousIssues };
            },
            onError: (err, variables, context) => {
                // If the mutation fails, use the context returned from onMutate to roll back
                queryClient.setQueryData(PrimeQueryKeys.workflowProblems, context?.previousIssues);
                console.error("Failed to update ball in court:", err);
            },
            onSettled: () => {
                // Always refetch after error or success to ensure server state
                queryClient.invalidateQueries(PrimeQueryKeys.workflowProblems);
            },
        },
    );

    // Handler functions
    const handleSeverityChange = (newSeverity: ConflictLikelihood) => {
        updateSeverityMutation.mutate({
            id: issue.id,
            severity: newSeverity,
        });
    };

    const handleStatusChange = (newStatus: IssueStatus) => {
        updateStatusMutation.mutate({
            id: issue.id,
            status: newStatus,
        });
    };

    const handleBallInCourtChange = (newBallInCourt: BallInCourt) => {
        updateBallInCourtMutation.mutate({
            id: issue.id,
            ballInCourt: newBallInCourt,
        });
    };

    const cardStyles = isDismissed ? { ...styles.issueCard, opacity: 0.7 } : styles.issueCard;
    const contentId = isDismissed ? `dismissed-problem-${index}` : `problem-${index}`;

    return (
        <Card sx={cardStyles}>
            <Stack direction="row" justifyContent="space-between">
                <Stack alignItems="center" direction="row" spacing={1}>
                    <Chip variant="soft" size="sm" sx={{ transform: "translateY(-1px)" }}>
                        #{issue.issueNumber}
                    </Chip>
                    <Typography level="title-md" sx={styles.issueTitle}>
                        {issue.title}
                    </Typography>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip>{issue.category}</Chip>
                    <Select
                        value={issue.severity as ConflictLikelihood}
                        onChange={(_, newValue) => {
                            if (newValue) {
                                handleSeverityChange(newValue);
                            }
                        }}
                        variant="plain"
                        size="sm"
                        renderValue={(selected) => (
                            <ConflictLikelihoodChip sev={selected?.value as ConflictLikelihood} />
                        )}
                        disabled={false}
                    >
                        <Option value="high">
                            <ConflictLikelihoodChip sev="high" />
                        </Option>
                        <Option value="medium">
                            <ConflictLikelihoodChip sev="medium" />
                        </Option>
                        <Option value="low">
                            <ConflictLikelihoodChip sev="low" />
                        </Option>
                    </Select>
                </Stack>
            </Stack>

            <Divider />

            {/* Status, Ball in Court, and Updated Info Row */}
            <Stack direction="row" alignItems="center" spacing={3} flexWrap="wrap">
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography level="body-sm" sx={{ minWidth: "50px" }}>
                        Status:
                    </Typography>
                    <Select
                        value={(issue.status as IssueStatus) || ISSUE_STATUS.OPEN}
                        onChange={(_, newValue) => {
                            if (newValue) {
                                handleStatusChange(newValue);
                            }
                        }}
                        variant="plain"
                        size="sm"
                        renderValue={(selected) => (
                            <IssueStatusChip status={selected?.value as IssueStatus} />
                        )}
                        disabled={false}
                    >
                        <Option value={ISSUE_STATUS.NOT_AN_ISSUE}>
                            <IssueStatusChip status={ISSUE_STATUS.NOT_AN_ISSUE} />
                        </Option>
                        <Option value={ISSUE_STATUS.FORWARD_TO_DESIGN_TEAM}>
                            <IssueStatusChip status={ISSUE_STATUS.FORWARD_TO_DESIGN_TEAM} />
                        </Option>
                        <Option value={ISSUE_STATUS.FORWARD_TO_SUB}>
                            <IssueStatusChip status={ISSUE_STATUS.FORWARD_TO_SUB} />
                        </Option>
                        <Option value={ISSUE_STATUS.RESOLVED_PENDING_REVISION}>
                            <IssueStatusChip status={ISSUE_STATUS.RESOLVED_PENDING_REVISION} />
                        </Option>
                        <Option value={ISSUE_STATUS.CLOSED}>
                            <IssueStatusChip status={ISSUE_STATUS.CLOSED} />
                        </Option>
                        <Option value={ISSUE_STATUS.OPEN}>
                            <IssueStatusChip status={ISSUE_STATUS.OPEN} />
                        </Option>
                    </Select>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography level="body-sm" sx={{ minWidth: "80px" }}>
                        Ball in court:
                    </Typography>
                    <Select
                        value={(issue.ballInCourt as BallInCourt) || "N/A"}
                        onChange={(_, newValue) => {
                            if (newValue) {
                                handleBallInCourtChange(newValue);
                            }
                        }}
                        variant="plain"
                        size="sm"
                        renderValue={(selected) => (
                            <BallInCourtChip ballInCourt={selected?.value as BallInCourt} />
                        )}
                        disabled={false}
                    >
                        <Option value="Design Team">
                            <BallInCourtChip ballInCourt="Design Team" />
                        </Option>
                        <Option value="Owner">
                            <BallInCourtChip ballInCourt="Owner" />
                        </Option>
                        <Option value="Subcontractor">
                            <BallInCourtChip ballInCourt="Subcontractor" />
                        </Option>
                        <Option value="Contractor">
                            <BallInCourtChip ballInCourt="Contractor" />
                        </Option>
                        <Option value="N/A">
                            <BallInCourtChip ballInCourt="N/A" />
                        </Option>
                    </Select>
                </Stack>

                {/* Updated Information */}
                {formatUpdatedInfo(issue) && (
                    <Typography level="body-xs" sx={{ color: "var(--joy-palette-neutral-600)" }}>
                        {formatUpdatedInfo(issue)}
                    </Typography>
                )}
            </Stack>

            <Divider />

            <CardContent>
                <MarkdownRenderer
                    message={issue.description}
                    contentId={contentId}
                    currentFileViewerFile={currentFileViewerFile}
                    setCurrentFileViewerFile={setCurrentFileViewerFile}
                />
            </CardContent>

            <Box sx={styles.previewContainer}>
                <ProblemNode
                    node={issue.node1}
                    setCurrentFileViewerFile={setCurrentFileViewerFile}
                />
                <ProblemNode
                    node={issue.node2}
                    setCurrentFileViewerFile={setCurrentFileViewerFile}
                />
            </Box>

            <Divider />

            <CardActions buttonFlex="0 1 140px" sx={styles.draftRfiButton}>
                <Button
                    startDecorator={<EditNoteOutlined />}
                    size="sm"
                    color="neutral"
                    variant="plain"
                    onClick={() => onRfiClick(issue)}
                >
                    Draft RFI
                </Button>
            </CardActions>
        </Card>
    );
}
